import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import { ROUTES, ROUTE_IDS } from '@/common/auth-routes';
import { TenantProvider } from '@/contexts/tenant-context';
import { AuthErrorPage } from '@/features/auth';
import { EnrollmentInvitationPage } from '@/features/enrollment-invitation';
import { OnboardingPage, TenantOnboardingPage } from '@/features/onboarding';
import { PAIR_ROUTE_SEGMENT } from '@/utils/pairing-utils';
import { ApplicationsRoute } from './components/applications-route';
import { HomeRedirect } from './components/home-redirect';
import { NotFoundPage } from './components/not-found-page';
import { TenantSelectionPage } from './components/tenant-selection-page';
import { PortalLayout } from './layouts/portal-layout';
import { RootLayout } from './layouts/root-layout';
import { authLoader, authShouldRevalidate } from './loaders/auth-loader';

// Lazy load pages for better code splitting
const ProfilePage = lazy(() => import('@/pages/profile-page'));
const DocsPage = lazy(() => import('@/features/docs/pages/docs-page'));
const AccountSettingsPage = lazy(() => import('@/pages/account-settings-page'));
const AgentsPage = lazy(() => import('@/features/agents/pages/agents-page'));
const AgentDetailPage = lazy(
  () => import('@/features/agents/pages/agent-detail-page')
);
const PairingPage = lazy(
  () => import('@/features/onboarding/pages/pairing-page')
);
const McpTesterPage = lazy(
  () => import('@/features/mcp-tester/pages/mcp-tester-page')
);

export const router = createBrowserRouter([
  {
    path: '/',
    element: <RootLayout />,
    children: [
      // Root path - redirect to tenant selection
      {
        index: true,
        element: <Navigate to={ROUTES.TENANT_SELECTION} replace />,
      },
      // Tenant selection page
      {
        path: ROUTES.TENANT_SELECTION.slice(1), // Remove leading slash for relative path
        element: <TenantSelectionPage />,
      },
      // Onboarding route (unauthenticated - for invitation magic links)
      {
        path: `${ROUTES.ONBOARDING.slice(1)}/:token`,
        element: <OnboardingPage />,
      },
      // Tenant onboarding route (unauthenticated - for tenant owner onboarding)
      {
        path: `${ROUTES.TENANT_ONBOARDING.slice(1)}/:token`,
        element: <TenantOnboardingPage />,
      },
      // Authenticator enrollment invitation (unauthenticated - admin-issued)
      {
        path: `${ROUTES.ENROLLMENT_INVITATION.slice(1)}/:token`,
        element: <EnrollmentInvitationPage />,
      },
      // Tenant-specific routes
      {
        path: ':tenant',
        id: ROUTE_IDS.TENANT_ROOT,
        element: <TenantProvider />,
        loader: authLoader,
        shouldRevalidate: authShouldRevalidate,
        children: [
          {
            index: true,
            element: <HomeRedirect />,
          },
          {
            path: ROUTES.AUTH_ERROR.slice(1),
            element: <AuthErrorPage />,
          },
          // Onboarding routes (unauthenticated - tenant-prefixed magic links)
          {
            path: `${ROUTES.ONBOARDING.slice(1)}/:token`,
            element: <OnboardingPage />,
          },
          {
            path: `${ROUTES.TENANT_ONBOARDING.slice(1)}/:token`,
            element: <TenantOnboardingPage />,
          },
          {
            path: `${ROUTES.ENROLLMENT_INVITATION.slice(1)}/:token`,
            element: <EnrollmentInvitationPage />,
          },
          // Tenant-scoped pairing invitation (unauthenticated - desktop QR target)
          {
            path: `${PAIR_ROUTE_SEGMENT}/:pairingCode`,
            element: (
              <Suspense fallback={null}>
                <PairingPage />
              </Suspense>
            ),
          },
          // Authenticated portal routes with header and sidebar
          {
            path: '',
            element: <PortalLayout />,
            children: [
              {
                path: ROUTES.DASHBOARD.slice(1),
                element: <ApplicationsRoute />,
              },
              {
                path: ROUTES.AGENTS.slice(1),
                element: <AgentsPage />,
              },
              {
                path: `${ROUTES.AGENTS.slice(1)}/:agentId`,
                element: <AgentDetailPage />,
              },
              {
                path: ROUTES.MCP_TESTER.slice(1),
                element: <McpTesterPage />,
              },
              {
                path: ROUTES.ACCOUNT_SETTINGS.slice(1),
                element: <AccountSettingsPage />,
              },
              {
                path: ROUTES.PROFILE.slice(1),
                element: <ProfilePage />,
              },
              {
                path: `${ROUTES.DOCS.slice(1)}/*`,
                element: <DocsPage />,
              },
            ],
          },
          // Catch-all for unknown routes within a tenant
          {
            path: '*',
            element: <NotFoundPage />,
          },
        ],
      },
      // Catch-all route for paths that don't match tenant pattern
      {
        path: '*',
        element: <NotFoundPage />,
      },
    ],
  },
]);
